/**
 * src/lib/webauthn.ts
 * ブラウザ側の WebAuthn（パスキー）ヘルパー。
 * サーバー（@simplewebauthn/server）が返す JSON オプションを navigator.credentials に渡し、
 * 結果をサーバーが検証できる JSON に直す。検証そのものは必ずサーバー側で行う。
 */

type AnyJson = Record<string, any>

function b64uToBuf(s: string): ArrayBuffer {
  const pad = '='.repeat((4 - (s.length % 4)) % 4)
  const bin = atob(s.replace(/-/g, '+').replace(/_/g, '/') + pad)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out.buffer
}

function bufToB64u(buf: ArrayBuffer): string {
  let bin = ''
  for (const b of new Uint8Array(buf)) bin += String.fromCharCode(b)
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** この端末・ブラウザでパスキーが使えるか（API の有無） */
export function isPasskeySupported(): boolean {
  return typeof window !== 'undefined'
    && !!window.PublicKeyCredential
    && !!navigator.credentials
    && window.isSecureContext
}

/** 端末内蔵の認証器（Windows Hello / Touch ID / 顔認証 等）が使えるか。無くてもセキュリティキーやスマホで可 */
export async function isPlatformAuthenticatorAvailable(): Promise<boolean> {
  try {
    if (!isPasskeySupported()) return false
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
  } catch {
    return false
  }
}

/** ユーザーが取り消した / タイムアウトした場合 */
export class PasskeyCancelled extends Error {
  constructor(message = '認証がキャンセルされました') {
    super(message)
    this.name = 'PasskeyCancelled'
  }
}

export function describePasskeyError(e: unknown): string {
  const name = (e as any)?.name as string | undefined
  if (e instanceof PasskeyCancelled || name === 'NotAllowedError' || name === 'AbortError') {
    return '認証がキャンセルされたか、時間切れになりました。もう一度お試しください。'
  }
  if (name === 'InvalidStateError') {
    return 'この端末のパスキーはすでに登録されています。別の端末で登録するか、既存のパスキーをご利用ください。'
  }
  if (name === 'NotSupportedError' || name === 'SecurityError') {
    return 'この端末またはブラウザではパスキーを利用できません。'
  }
  return (e as any)?.message ?? 'パスキーの操作に失敗しました。'
}

/**
 * パスキーの保存先の希望（画面の案内のためだけのヒント。サーバーの検証条件は変わらない）
 *   'phone'  … パソコンの画面から、自分のスマートフォンに保存する（QRコード → スマホで顔認証・指紋）。
 *              Windows Hello など、そのパソコン内蔵の保存先を選択肢から外す
 *   'device' … 今使っている端末（このスマホ／本人専用のパソコン）に保存する
 *   省略     … ブラウザの標準の選択画面
 */
export type PasskeyTarget = 'phone' | 'device'

/** サーバーの登録オプション → 端末に新しいパスキーを作成 → サーバーへ送る JSON */
export async function createPasskey(options: AnyJson, target?: PasskeyTarget): Promise<AnyJson> {
  const publicKey: any = {
    ...options,
    challenge: b64uToBuf(options.challenge),
    user: { ...options.user, id: b64uToBuf(options.user.id) },
    excludeCredentials: (options.excludeCredentials ?? []).map((c: AnyJson) => ({ ...c, id: b64uToBuf(c.id) })),
  }
  if (target === 'phone') {
    // 'cross-platform' = パソコン内蔵ではない認証器（スマホ／セキュリティキー）。hints は対応ブラウザでQR画面を直接開く
    publicKey.authenticatorSelection = { ...options.authenticatorSelection, authenticatorAttachment: 'cross-platform' }
    publicKey.hints = ['hybrid']
  } else if (target === 'device') {
    publicKey.hints = ['client-device']
  }
  const cred = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential | null
  if (!cred) throw new PasskeyCancelled()
  const r = cred.response as AuthenticatorAttestationResponse
  return {
    id: cred.id,
    rawId: bufToB64u(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bufToB64u(r.clientDataJSON),
      attestationObject: bufToB64u(r.attestationObject),
      transports: typeof r.getTransports === 'function' ? r.getTransports() : [],
    },
    clientExtensionResults: cred.getClientExtensionResults(),
    authenticatorAttachment: cred.authenticatorAttachment ?? undefined,
  }
}

/** サーバーの認証オプション → 端末の認証ダイアログ → サーバーへ送る JSON */
export async function getPasskeyAssertion(options: AnyJson): Promise<AnyJson> {
  const publicKey: any = {
    ...options,
    challenge: b64uToBuf(options.challenge),
    allowCredentials: (options.allowCredentials ?? []).map((c: AnyJson) => ({ ...c, id: b64uToBuf(c.id) })),
  }
  const cred = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential | null
  if (!cred) throw new PasskeyCancelled()
  const r = cred.response as AuthenticatorAssertionResponse
  return {
    id: cred.id,
    rawId: bufToB64u(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bufToB64u(r.clientDataJSON),
      authenticatorData: bufToB64u(r.authenticatorData),
      signature: bufToB64u(r.signature),
      userHandle: r.userHandle ? bufToB64u(r.userHandle) : undefined,
    },
    clientExtensionResults: cred.getClientExtensionResults(),
    authenticatorAttachment: cred.authenticatorAttachment ?? undefined,
  }
}
